import { ComponentProps, PropsWithChildren } from "react";
import { ArrowUpRight } from "@tamagui/lucide-icons";
import { styled, Text, View, XStack } from "tamagui";

import { withIconRef } from "../../../utils";
import { RenderMd } from "../../ui";
import { Card } from "../../ui/card";
import { Progress } from "../../ui/progress";

const ScoreText = styled(Text, {
  name: "ScoreText",
  fontFamily: "$subtitle",
  variants: {
    state: {
      success: {
        color: "$score-10-9",
      },
      warning: {
        color: "$warning-500",
      },
      neutral: {
        color: "$gray-600",
      },
      danger: {
        color: "$error-500",
      },
    },
    font: {
      lg: {
        fontWeight: "$5",
        fontSize: "$9",
        lineHeight: "$9",
      },

      md: {
        fontWeight: "$5",
        fontSize: "$6.8",
        lineHeight: "$6.8",
      },
    },
  } as const,
  defaultVariants: {
    state: "neutral",
    font: "lg",
  },
});

const ScoreContainer = styled(Text, {
  name: "ScoreContainer",
  fontFamily: "$subtitle",
  color: "$gray-100",
  variants: {
    font: {
      lg: {
        fontWeight: "$5",
        fontSize: "$9",
        lineHeight: "$9",
      },

      md: {
        fontWeight: "$5",
        fontSize: "$6.8",
        lineHeight: "$6.8",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

type ScoreState = ComponentProps<typeof ScoreText>["state"];
type ScoreFont = ComponentProps<typeof ScoreText>["font"];

const Score: React.FC<
  PropsWithChildren & { max?: number; state: ScoreState; font?: ScoreFont }
> = ({ state, max = 100, font = "lg", children }) => {
  return (
    <ScoreContainer font={font}>
      <ScoreText font={font} state={state}>
        {children}
      </ScoreText>
      /{max}
    </ScoreContainer>
  );
};

type ContainerProps = ComponentProps<typeof Card>;

const Container: React.FC<ContainerProps> = (props) => {
  return (
    <Card
      variant="default"
      shadow="md"
      paddingHorizontal={"$8"}
      paddingVertical={"$5"}
      gap={"$3"}
      $sm={{
        marginHorizontal: "$3",
        paddingHorizontal: "$3",
        paddingVertical: "$2",
        gap: "$2",
      }}
      {...props}
    />
  );
};

const Title = styled(Text, {
  name: "ProgressCardTitle",
  fontFamily: "$subtitle",
  color: "$gray-primary-900",
  variants: {
    font: {
      lg: {
        fontWeight: "$6",
        fontSize: "$7",
        lineHeight: "$7",
      },
      md: {
        fontWeight: "$6",
        fontSize: "$6",
        lineHeight: "$6",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

type DescriptionProps = ComponentProps<typeof RenderMd> & {
  font?: "md" | "lg";
};

const Description: React.FC<DescriptionProps> = (props) => {
  return <RenderMd {...props} />;
};

const DynamicText = styled(Text, {
  name: "DynamicText",
  fontFamily: "$subtitle",
  variants: {
    state: {
      success: {
        color: "$score-10-9",
      },
      warning: {
        color: "$warning-500",
      },
      neutral: {
        color: "$gray-600",
      },
      danger: {
        color: "$error-500",
      },
    },
    font: {
      lg: {
        fontWeight: "$5",
        fontSize: "$5",
        lineHeight: "$5",
      },
      md: {
        fontWeight: "$5",
        fontSize: "$3",
        lineHeight: "$3",
      },
    },
  } as const,
});

const Arrow = styled(withIconRef(ArrowUpRight), {
  name: "Arrow",
  variants: {
    size: {
      md: {
        height: "$2.5",
        width: "$2.5",
      },
    },

    state: {
      success: {
        color: "$score-10-9",
      },
      warning: {
        color: "$warning-500",
        rotate: "95deg",
      },
      neutral: {
        display: "none",
      },
      danger: {
        rotate: "95deg",
        color: "$error-500",
      },
    },
  } as const,
  defaultVariants: {
    size: "md",
  },
});

//TODO: should be moved to ui
const Separator = styled(View, {
  name: "Separator",
  backgroundColor: "$gray-200",
  variants: {
    variant: {
      horizontal: {
        height: "$0.1",
        width: "100%",
      },
      vertical: {
        height: "100$",
        width: "$0.1",
      },
    },
  } as const,
  defaultVariants: {
    variant: "horizontal",
  },
});

type DynamicsState = ComponentProps<typeof DynamicText>["state"];

const Dynamics: React.FC<{
  state: DynamicsState;
  label: string | null | undefined;
  labelState: DynamicsState;
  description: string | null | undefined;
  value: string | null | undefined;
}> = ({ state, label, labelState, value, description }) => {
  return (
    <View>
      <DynamicText
        data-id={"diagnostic-label"}
        font="lg"
        $sm={{
          fontSize: "$2",
          lineHeight: "$2",
          fontWeight: "$6",
        }}
        state={labelState}
      >
        {label}
      </DynamicText>
      <XStack alignItems="center" gap={"$0.5"}>
        <XStack alignItems="center">
          <Arrow state={state} />
          <DynamicText
            data-id={"diagnostic-dynamics"}
            $sm={{
              fontSize: "$2",
              lineHeight: "$2",
              fontWeight: "$4",
            }}
            font="md"
            state={state}
          >
            {value}
          </DynamicText>
        </XStack>
        <DynamicText
          data-id={"diagnostic-last-checkup"}
          $sm={{
            fontSize: "$2",
            lineHeight: "$2",
            fontWeight: "$4",
          }}
          font="md"
          state={"neutral"}
        >
          {description}
        </DynamicText>
      </XStack>
    </View>
  );
};

export const ProgressCard = {
  Container,
  Score,
  Progress,
  Title,
  Description,
  Dynamics,
  Separator,
};
